import { useCallback, useEffect, useState } from 'react';
import { Box, MenuItem, Select as MUISelect, Tooltip } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import {
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useUserAccess,
} from '@ui-framework/index';
import SxclServicesAutocomplete from '../SxclServicesAutocomplete';
import { ISSUE_API_ROOT, SXCL_SERVICES_API_ROOT } from '../constants';
import {
  getSearchPlaceholder,
  getLookupOptions,
  getProviderName,
  getIssueUserOptionLabel,
  getCurrentUserId,
  getReporterDisplayLabel,
  getLocalizedLookupLabel,
  pinCurrentUserOption,
} from '../utils';
import {
  ISSUE_LOOKUP_TOOLTIP_PLACEMENT,
  applyTooltipOverrides,
  buildCompanyParams,
  sortOptionsByLabel,
} from './utils';

function IssueSelect({
  name,
  options,
  placeholder,
  permission,
  lookupCategory,
}) {
  const { control } = useFormContext();
  const { formatMessage } = useLocalization();
  // Honor backend field permissions (framework fields do this natively;
  // custom selects must opt in).
  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(permission, '');
  const resolvedPlaceholder =
    placeholder ??
    formatMessage({ id: 'PLEASE_SELECT', defaultMessage: 'Please select' });

  return (
    <Box sx={{ mt: '-5px' }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MUISelect
            displayEmpty
            size="small"
            fullWidth
            disabled={readOnly}
            value={field.value || ''}
            onChange={field.onChange}
            renderValue={selected =>
              selected ? (
                options.find(option => option.value === selected)?.label ||
                // Options not loaded yet (or lookup call failed): localize the
                // raw code instead of showing the humanized English label.
                getLocalizedLookupLabel(selected, lookupCategory, formatMessage)
              ) : (
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  {resolvedPlaceholder}
                </Box>
              )
            }>
            <MenuItem value="">
              <Box component="span" sx={{ color: 'text.disabled' }}>
                {resolvedPlaceholder}
              </Box>
            </MenuItem>
            {options.map(option =>
              option.tooltip ? (
                <MenuItem key={option.value} value={option.value}>
                  <Tooltip
                    title={option.tooltip}
                    placement={ISSUE_LOOKUP_TOOLTIP_PLACEMENT}
                    arrow>
                    <Box component="span" sx={{ width: '100%' }}>
                      {option.label}
                    </Box>
                  </Tooltip>
                </MenuItem>
              ) : (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ),
            )}
          </MUISelect>
        )}
      />
    </Box>
  );
}

function IssueLookupSelect({
  excludedCodes,
  includedCodes,
  name,
  field,
  permission,
  tooltipByCode,
}) {
  const dataProvider = useDataProvider(getProviderName());
  const { formatMessage } = useLocalization();
  const [lookups, setLookups] = useState([]);
  const { lookupCategory } = field;
  const resolvedExcludedCodes = excludedCodes || field.excludedCodes || [];
  const resolvedIncludedCodes = includedCodes || field.includedCodes || [];
  const excludedCodesKey = resolvedExcludedCodes.join('|');
  const includedCodesKey = resolvedIncludedCodes.join('|');

  const loadOptions = useCallback(async () => {
    const excludedCodeSet = new Set(resolvedExcludedCodes);
    const includedCodeSet = new Set(resolvedIncludedCodes);
    const result = await dataProvider.get(`${ISSUE_API_ROOT}/lookups`, {
      category: lookupCategory,
    });

    if (result.ok) {
      setLookups(
        (result.data?.data || [])
          .filter(lookup => !excludedCodeSet.has(lookup.code))
          .filter(
            lookup =>
              includedCodeSet.size === 0 || includedCodeSet.has(lookup.code),
          ),
      );
    }
  }, [dataProvider, excludedCodesKey, includedCodesKey, lookupCategory]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const options = getLookupOptions(lookups, lookupCategory, formatMessage);
  let resolvedOptions = applyTooltipOverrides(options, tooltipByCode);
  // Lookups arrive in workflow order; schemas opt in to alphabetical order
  // where that reads better (e.g. the search Status filter).
  if (field.sortByLabel) {
    resolvedOptions = sortOptionsByLabel(resolvedOptions);
  }

  return (
    <IssueSelect
      name={name}
      options={resolvedOptions}
      permission={permission}
      lookupCategory={lookupCategory}
    />
  );
}

const useIssueTypeHelperText = () => {
  const { formatMessage } = useLocalization();

  return {
    BUG: formatMessage({
      id: 'ISSUE_TYPE_HELP_ISSUE',
      defaultMessage:
        'Use this request type to report a defect in the system, such as errors, incorrect behavior, or features that are not functioning as intended. Include steps to reproduce, expected vs. actual results, and any relevant screenshots or logs.',
    }),
    ENHANCEMENT_REQUEST: formatMessage({
      id: 'ISSUE_TYPE_HELP_ENHANCEMENT_REQUEST',
      defaultMessage:
        'Use this request type to request improvements to existing features or workflows. Clearly describe the current behavior, the desired enhancement, and the expected benefit or business value.',
    }),
    FEATURE_REQUEST: formatMessage({
      id: 'ISSUE_TYPE_HELP_FEATURE_REQUEST',
      defaultMessage:
        'Use this request type to propose entirely new functionality that does not currently exist. Describe the use case, expected outcome, and the value it would provide to users or the business.',
    }),
    SUPPORT: formatMessage({
      id: 'ISSUE_TYPE_HELP_SUPPORT_REQUEST',
      defaultMessage:
        'Use this request type for general support needs, questions, or assistance with using the system. Include relevant context, screenshots, or examples to help resolve your request quickly.',
    }),
  };
};

// The full help copy is the option hover text (replacing the short lookup
// description) so it no longer takes vertical space beneath the dropdown.
export function IssueTypeSelect({ field, name, ...props }) {
  const helpTextByCode = useIssueTypeHelperText();

  return (
    <IssueLookupSelect
      {...props}
      name={name}
      field={{ ...field, lookupCategory: 'TYPE' }}
      tooltipByCode={helpTextByCode}
    />
  );
}

export function IssuePrioritySelect({ field, ...props }) {
  return (
    <IssueLookupSelect
      {...props}
      field={{ ...field, lookupCategory: 'PRIORITY' }}
    />
  );
}

// Component options that require the service administrator to have purchased
// the matching module.
const PURCHASED_COMPONENT_FLAGS = {
  CLAIMS: 'claimsPurchased',
  DISPATCH: 'dispatchPurchased',
  INSIGHTS: 'insightPurchased',
};

// Cached per company so a login as a different user (or SA switch) refetches;
// failures are not cached so the next mount retries instead of leaving the
// component list unfiltered for the rest of the session.
let moduleSettingsCache = null;

const loadModuleSettings = (dataProvider, cacheKey) => {
  if (!moduleSettingsCache || moduleSettingsCache.key !== cacheKey) {
    const promise = dataProvider
      .get(`${SXCL_SERVICES_API_ROOT}/service-modules`)
      .then(result => (result?.ok ? result.data?.data || result.data : null))
      .catch(() => null)
      .then(moduleSettings => {
        if (!moduleSettings) {
          moduleSettingsCache = null;
        }
        return moduleSettings;
      });
    moduleSettingsCache = { key: cacheKey, promise };
  }
  return moduleSettingsCache.promise;
};

export function IssueComponentSelect({ field, ...props }) {
  const userAccess = useUserAccess();
  const sxclServicesDataProvider = useDataProvider('sxclServices');
  const isAdmin = userAccess.isAdmin?.() ?? false;
  const companyId = userAccess?.company?.id || '';
  const [unpurchasedCodes, setUnpurchasedCodes] = useState([]);

  useEffect(() => {
    let active = true;
    if (isAdmin) {
      setUnpurchasedCodes([]);
      return undefined;
    }

    loadModuleSettings(sxclServicesDataProvider, companyId).then(
      moduleSettings => {
        if (!active || !moduleSettings) {
          return;
        }
        setUnpurchasedCodes(
          Object.entries(PURCHASED_COMPONENT_FLAGS)
            .filter(([, flag]) => !moduleSettings[flag])
            .map(([code]) => code),
        );
      },
    );

    return () => {
      active = false;
    };
  }, [companyId, isAdmin, sxclServicesDataProvider]);

  return (
    <IssueLookupSelect
      {...props}
      excludedCodes={unpurchasedCodes}
      field={{ ...field, lookupCategory: 'COMPONENT' }}
    />
  );
}

export function IssueStatusSelect({
  excludedCodes,
  includedCodes,
  field,
  ...props
}) {
  return (
    <IssueLookupSelect
      {...props}
      excludedCodes={excludedCodes}
      includedCodes={includedCodes}
      field={{ ...field, lookupCategory: 'STATUS' }}
    />
  );
}

export function IssueResolutionSelect({ field, ...props }) {
  return (
    <IssueLookupSelect
      {...props}
      field={{ ...field, lookupCategory: 'RESOLUTION_CODE' }}
    />
  );
}

const ACTIVITY_TYPE_OPTIONS = [
  { label: 'Created', value: 'CREATED' },
  { label: 'Updated', value: 'UPDATED' },
  { label: 'Status changed', value: 'STATUS_CHANGED' },
  { label: 'Priority changed', value: 'PRIORITY_CHANGED' },
  { label: 'Title changed', value: 'TITLE_CHANGED' },
  { label: 'Comment added', value: 'COMMENT_ADDED' },
  { label: 'Link added', value: 'LINK_ADDED' },
  { label: 'Link removed', value: 'LINK_REMOVED' },
  { label: 'Attachment added', value: 'ATTACHMENT_ADDED' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'Reopened', value: 'REOPENED' },
];

export function IssueActivityTypeSelect({ name }) {
  const { formatMessage } = useLocalization();
  const options = ACTIVITY_TYPE_OPTIONS.map(option => ({
    ...option,
    label: formatMessage({
      id: `ISSUES_ACTIVITY_TYPE_${option.value}`,
      defaultMessage: option.label,
    }),
  }));

  return <IssueSelect name={name} options={options} />;
}

const buildIssueCompanyParams = searchValue => ({
  page: 1,
  limit: 20,
  ...(searchValue && { companyName: searchValue }),
});

const buildServiceAdministratorParams = searchValue => ({
  page: 1,
  limit: 20,
  ...(searchValue && { serviceAdministratorName: searchValue }),
});

const buildReporterParams = (searchValue, companyId) => ({
  page: 1,
  limit: 20,
  sortBy: 'email:ASC',
  reportersOnly: true,
  ...(companyId && { companyId }),
  ...(searchValue && { email: searchValue }),
});

const buildAssigneeParams = searchValue => ({
  page: 1,
  limit: 20,
  assignableOnly: true,
  ...(searchValue && { email: searchValue }),
});

const buildAssigneeValueParams = userId => ({
  page: 1,
  limit: 1,
  assignableOnly: true,
  userId,
});

const getCompanyLabel = company =>
  company.name ||
  company.displayName ||
  company.companyName ||
  company.companyNumberInternal ||
  company.companyNumber ||
  company.label ||
  company.id ||
  '';

const getReporterCompanyLabel = company =>
  company.companyName ||
  company.companyNumberInternal ||
  company.companyNumber ||
  company.label ||
  company.id ||
  '';

const getServiceAdministratorLabel = serviceAdministrator =>
  serviceAdministrator.serviceAdministratorName ||
  serviceAdministrator.serviceAdministratorAbbreviation ||
  serviceAdministrator.label ||
  serviceAdministrator.id ||
  '';

const getReporterLabel = user => getReporterDisplayLabel(user);

const REPORTER_COMPANY_TYPE_OPTIONS = [
  {
    label: 'Service Administrator',
    localeId: 'SERVICE_ADMINISTRATOR',
    value: 'SERVICE_ADMINISTRATOR',
  },
  {
    label: 'Service Provider',
    localeId: 'SERVICE_PROVIDER',
    value: 'SERVICE_PROVIDER',
  },
  { label: 'Admin', localeId: 'ADMIN', value: 'ADMIN' },
];

function IssueEntityAutocomplete({
  name,
  endpoint,
  getOptionLabel,
  buildParams,
  buildValueParams,
  noOptionsText,
  providerName = getProviderName(),
  minSearchLength,
  searchPromptText,
  transformOptions,
  placeholder,
}) {
  const dataProvider = useDataProvider(providerName);
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SxclServicesAutocomplete
          dataProvider={dataProvider}
          endpoint={endpoint}
          placeholder={placeholder}
          value={field.value || ''}
          onChange={field.onChange}
          getOptionLabel={getOptionLabel}
          buildParams={buildParams}
          buildValueParams={buildValueParams}
          noOptionsText={noOptionsText}
          minSearchLength={minSearchLength}
          searchPromptText={searchPromptText}
          transformOptions={transformOptions}
        />
      )}
    />
  );
}

export function IssueCompanySelector(props) {
  const { formatMessage } = useLocalization();
  return (
    <IssueEntityAutocomplete
      {...props}
      endpoint={`${SXCL_SERVICES_API_ROOT}/companies`}
      providerName="sxclServices"
      getOptionLabel={getCompanyLabel}
      buildParams={buildCompanyParams}
      noOptionsText={formatMessage({
        id: 'NO_COMPANIES_FOUND',
        defaultMessage: 'No companies found',
      })}
    />
  );
}

export function IssueReporterCompanySelector(props) {
  const { formatMessage } = useLocalization();
  return (
    <IssueEntityAutocomplete
      {...props}
      endpoint={`${ISSUE_API_ROOT}/companies`}
      getOptionLabel={getReporterCompanyLabel}
      buildParams={buildIssueCompanyParams}
      placeholder={getSearchPlaceholder(formatMessage)}
      noOptionsText={formatMessage({
        id: 'NO_COMPANIES_FOUND',
        defaultMessage: 'No companies found',
      })}
    />
  );
}

export function IssueReporterCompanyTypeSelect({ name }) {
  const userAccess = useUserAccess();
  const { formatMessage } = useLocalization();
  const options = (
    userAccess.isAdmin?.()
      ? REPORTER_COMPANY_TYPE_OPTIONS
      : REPORTER_COMPANY_TYPE_OPTIONS.filter(option => option.value !== 'ADMIN')
  ).map(option => ({
    ...option,
    label: formatMessage({
      id: option.localeId,
      defaultMessage: option.label,
    }),
  }));

  return <IssueSelect name={name} options={options} />;
}

export function IssueServiceAdministratorSelector(props) {
  const { formatMessage } = useLocalization();
  return (
    <IssueEntityAutocomplete
      {...props}
      endpoint={`${SXCL_SERVICES_API_ROOT}/service-administrators`}
      providerName="sxclServices"
      getOptionLabel={getServiceAdministratorLabel}
      buildParams={buildServiceAdministratorParams}
      noOptionsText={formatMessage({
        id: 'NO_SERVICE_ADMINISTRATORS_FOUND',
        defaultMessage: 'No service administrators found',
      })}
    />
  );
}

export function IssueReporterSelector(props) {
  const { formatMessage } = useLocalization();
  const userAccess = useUserAccess();
  // SPs only see their own company; SAs and admins search every reporter in
  // their scope (the backend limits SA results to their own instance).
  const reporterCompanyId = userAccess.isSP?.()
    ? userAccess?.company?.id
    : undefined;
  const buildParams = useCallback(
    searchValue => buildReporterParams(searchValue, reporterCompanyId),
    [reporterCompanyId],
  );

  return (
    <IssueEntityAutocomplete
      {...props}
      endpoint={`${ISSUE_API_ROOT}/users`}
      getOptionLabel={getReporterLabel}
      buildParams={buildParams}
      placeholder={getSearchPlaceholder(formatMessage)}
      noOptionsText={formatMessage({
        id: 'NO_USERS_FOUND',
        defaultMessage: 'No users found',
      })}
    />
  );
}

export function IssueAssigneeSelector(props) {
  const { formatMessage } = useLocalization();
  const userAccess = useUserAccess();
  const currentUserId = getCurrentUserId(userAccess);
  const currentUserFirstName =
    userAccess?.firstName ?? userAccess?.user?.firstName;
  const currentUserLastName =
    userAccess?.lastName ?? userAccess?.user?.lastName;
  const currentUserEmail = userAccess?.email ?? userAccess?.user?.email;
  const transformOptions = useCallback(
    users =>
      pinCurrentUserOption(users, {
        id: currentUserId,
        firstName: currentUserFirstName,
        lastName: currentUserLastName,
        email: currentUserEmail,
      }),
    [
      currentUserEmail,
      currentUserFirstName,
      currentUserId,
      currentUserLastName,
    ],
  );
  const getAssigneeOptionLabel = useCallback(
    user => {
      const label = getIssueUserOptionLabel(user);
      return user?.isCurrentUser
        ? formatMessage(
            {
              id: 'ASSIGNED_TO_ME_OPTION',
              defaultMessage: 'Me - {userLabel}',
            },
            { userLabel: label },
          )
        : label;
    },
    [formatMessage],
  );

  return (
    <IssueEntityAutocomplete
      {...props}
      endpoint={`${ISSUE_API_ROOT}/users`}
      getOptionLabel={getAssigneeOptionLabel}
      buildParams={buildAssigneeParams}
      buildValueParams={buildAssigneeValueParams}
      transformOptions={transformOptions}
      placeholder={getSearchPlaceholder(formatMessage)}
      noOptionsText={formatMessage({
        id: 'NO_ASSIGNABLE_USERS_FOUND',
        defaultMessage: 'No assignable users found',
      })}
    />
  );
}
